import { useQuery } from "@tanstack/react-query"
import { SelectFilterBarField } from "design-system/molecules/FilterBar"
import { uniqueBy } from "lopermut"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useState } from "react"
import { OrganizationTypeEnum } from "schema"
import { getBackofficeCompaniesQuery } from "modules/backoffice/lib/companies/getBackofficeCompaniesQuery"
import { useGetBackofficeCompaniesQuery } from "./useGetBackofficeCompaniesQuery"

type Props = {
  label: string
  value: string | undefined
  onChange: (companyId: string | undefined) => void
  hasShopifyIntegration?: boolean
  organizationTypes?: Array<(typeof OrganizationTypeEnum)[keyof typeof OrganizationTypeEnum]>
}

const SORT_BY = { field: "name", order: "asc" } as const

export function useCompanyFilterField({
  label,
  value,
  onChange,
  hasShopifyIntegration = false,
  organizationTypes = [OrganizationTypeEnum.Client, OrganizationTypeEnum.Partner],
}: Props) {
  const [optionsSearch, setOptionsSearch] = useState("")
  const debouncedOptionsSearch = useDebouncedValue({ value: optionsSearch })

  const { data: companies = [] } = useGetBackofficeCompaniesQuery({
    query: {
      query: debouncedOptionsSearch || undefined,
      page: 1,
      total: 100,
      sortBy: SORT_BY,
      filterBy: { type: organizationTypes, hasAmphoraIntegration: false, hasShopifyIntegration },
    },
  })

  const { data: selectedCompanies = [] } = useQuery({
    ...getBackofficeCompaniesQuery({
      query: { query: value, page: 1, total: 10, sortBy: SORT_BY },
    }),
    enabled: !!value,
  })
  const selectedCompany = selectedCompanies.filter((company) => company.id === value)

  const field: SelectFilterBarField = {
    key: "companyId",
    type: "select",
    label,
    value,
    onChange,
    optionsSearch,
    onOptionsSearchChange: setOptionsSearch,
    options: uniqueBy([...selectedCompany, ...companies], "id").map((company) => ({
      value: company.id,
      label: company.name,
    })),
  }

  return field
}
